import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faTimes, faSync } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';

const AdminFawaterakTransaction = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const [filters, setFilters] = useState({
        TransactionId: '',
        Status: '',
        CreatedOn: '',
        IsActive: 'true'
    });

    const fetchTransactions = async (currentFilters = filters, page = pagination.pageNumber, size = pagination.pageSize) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (currentFilters.TransactionId) params.append('TransactionId', currentFilters.TransactionId);
            if (currentFilters.Status !== '') params.append('Status', currentFilters.Status);
            if (currentFilters.CreatedOn) params.append('CreatedOn', currentFilters.CreatedOn);
            if (currentFilters.IsActive !== '') params.append('IsActive', currentFilters.IsActive);
            
            params.append('PageNumber', page);
            params.append('PageSize', size);

            const res = await ApiServices.get(`Payment/Fawaterak/transactions?${params.toString()}`);
            
            if (res && res.data) {
                setTransactions(res.data.items || []);
                setPagination({
                    pageNumber: res.data.current_page || 1,
                    pageSize: res.data.page_size || 10,
                    totalPages: res.data.total_pages || 1,
                    totalRecords: res.data.total_records || 0
                });
            }
        } catch (e) {
            toastHelper.error('فشل في جلب المعاملات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, pageNumber: 1 }));
        fetchTransactions(filters, 1, pagination.pageSize);
    };

    const clearFilters = () => {
        const resetFilters = {
            TransactionId: '',
            Status: '',
            CreatedOn: '',
            IsActive: 'true'
        };
        setFilters(resetFilters);
        setPagination(prev => ({ ...prev, pageNumber: 1 }));
        fetchTransactions(resetFilters, 1, pagination.pageSize);
    };

    const handlePageChange = (newPage) => {
        fetchTransactions(filters, newPage, pagination.pageSize);
    };

    const handlePageSizeChange = (newSize) => {
        setPagination(prev => ({ ...prev, pageSize: newSize, pageNumber: 1 }));
        fetchTransactions(filters, 1, newSize);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'Pending': { className: 'status-pending', text: 'قيد الانتظار' },
            'Paid': { className: 'status-paid', text: 'مدفوع' },
            'Failed': { className: 'status-failed', text: 'فاشل' },
            'Cancelled': { className: 'status-cancelled', text: 'ملغي' },
            'Refunded': { className: 'status-refunded', text: 'مسترجع' }
        };
        const st = statusMap[status] || { className: 'status-default', text: status };
        return <span className={`status-badge ${st.className}`}>{st.text}</span>;
    };

    const columns = [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'fawaterak_transaction_id', label: 'Fawaterak Transaction ID' },
        { key: 'intent_key', label: 'Intent Key' },
        { key: 'invoice_id', label: 'Invoice ID' },
        { key: 'cart_total', label: 'Cart Total' },
        { key: 'paid_amount', label: 'Paid Amount' },
        { key: 'currency', label: 'Currency' },
        { 
            key: 'status', 
            label: 'Status',
            render: (val) => getStatusBadge(val)
        },
        { key: 'customer_email', label: 'Customer Email' },
        { key: 'customer_phone', label: 'Customer Phone' },
        { key: 'customer_name', label: 'Customer Name' },
        { 
            key: 'checkout_url', 
            label: 'Checkout URL',
            render: (val) => val ? <a href={val} target="_blank" rel="noreferrer" className="checkout-link">Checkout URL</a> : '-'
        },
        { key: 'merchant_reference_number', label: 'Merchant Reference Number' },
        { key: 'fawaterak_reference_number', label: 'Fawaterak Reference Number' },
        { key: 'created_on', label: 'Created On Date' },
        { key: 'created_on_time', label: 'Created On Time' },
        { key: 'paid_at', label: 'Paid At Date' },
        { key: 'paid_at_time', label: 'Paid At Time' },
        { key: 'user_id', label: 'User ID' },
        { key: 'user_full_name', label: 'User Full Name' }
    ];

    return (
        <div className="admin-transactions-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>معاملات فواتيرك</h2>
                    <p>إدارة وتتبع عمليات الدفع عبر بوابة فواتيرك</p>
                </div>
                <button className="refresh-btn" onClick={() => fetchTransactions()}>
                    <FontAwesomeIcon icon={faSync} className={loading ? "fa-spin" : ""} />
                    <span>تحديث</span>
                </button>
            </div>

            <div className="filters-card">
                <div className="filters-grid">
                    <div className="filter-group">
                        <label>رقم المعاملة</label>
                        <input
                            type="text"
                            name="TransactionId"
                            value={filters.TransactionId}
                            onChange={handleFilterChange}
                            placeholder="أدخل رقم المعاملة..."
                            className="form-control"
                        />
                    </div>
                    <div className="filter-group">
                        <label>الحالة</label>
                        <select
                            name="Status"
                            value={filters.Status}
                            onChange={handleFilterChange}
                            className="form-control"
                        >
                            <option value="">الكل</option>
                            <option value="0">قيد الانتظار</option>
                            <option value="1">مدفوع</option>
                            <option value="2">فاشل</option>
                            <option value="3">ملغي</option>
                            <option value="4">مسترجع</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>تاريخ الإنشاء</label>
                        <input
                            type="date"
                            name="CreatedOn"
                            value={filters.CreatedOn}
                            onChange={handleFilterChange}
                            className="form-control"
                        />
                    </div>
                    <div className="filter-group">
                        <label>نشط</label>
                        <select
                            name="IsActive"
                            value={filters.IsActive}
                            onChange={handleFilterChange}
                            className="form-control"
                        >
                            <option value="">الكل</option>
                            <option value="true">نعم</option>
                            <option value="false">لا</option>
                        </select>
                    </div>
                </div>
                <div className="filters-actions">
                    <button className="btn-apply" onClick={applyFilters}>
                        <FontAwesomeIcon icon={faFilter} />
                        تطبيق الفلاتر
                    </button>
                    <button className="btn-clear" onClick={clearFilters}>
                        <FontAwesomeIcon icon={faTimes} />
                        مسح
                    </button>
                </div>
            </div>

            <SoftGrid
                columns={columns}
                data={transactions}
                isLoading={loading}
                isPaginated={true}
                pageNumber={pagination.pageNumber}
                pageSize={pagination.pageSize}
                totalPages={pagination.totalPages}
                totalRecords={pagination.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                searchPlaceholder="ابحث في المعاملات..."
                useExportButtons={true}
                exportFilename="fawaterak_transactions"
                isColumnSelectable={true}
            />

            <style>{`
                .admin-transactions-container {
                    padding: 20px;
                    direction: rtl;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .header-info h2 {
                    margin: 0;
                    color: #1e293b;
                    font-size: 1.5rem;
                    font-weight: 800;
                }
                .header-info p {
                    margin: 4px 0 0;
                    color: #64748b;
                }
                .refresh-btn {
                    background: #f8fafc;
                    color: #475569;
                    border: 1px solid #e2e8f0;
                    padding: 10px 20px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .refresh-btn:hover {
                    background: #f1f5f9;
                    color: #1e293b;
                }
                
                .filters-card {
                    background: white;
                    border-radius: 16px;
                    padding: 20px;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                }
                .filters-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 20px;
                }
                .filter-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .filter-group label {
                    font-size: 0.9rem;
                    color: #475569;
                    font-weight: 600;
                }
                .form-control {
                    padding: 10px 14px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    color: #1e293b;
                    transition: border-color 0.2s;
                    font-family: inherit;
                }
                .form-control:focus {
                    outline: none;
                    border-color: #6366f1;
                    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
                }
                
                .filters-actions {
                    display: flex;
                    gap: 12px;
                    justify-content: flex-end;
                    border-top: 1px solid #f1f5f9;
                    padding-top: 16px;
                }
                .btn-apply {
                    background: #6366f1;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: background 0.2s;
                    font-family: inherit;
                }
                .btn-apply:hover {
                    background: #4f46e5;
                }
                .btn-clear {
                    background: #f1f5f9;
                    color: #64748b;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                    font-family: inherit;
                }
                .btn-clear:hover {
                    background: #e2e8f0;
                    color: #475569;
                }

                .customer-info, .date-info {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
                .customer-name, .date-text {
                    font-weight: 600;
                    color: #1e293b;
                }
                .customer-phone, .time-text, .text-muted {
                    font-size: 0.85rem;
                    color: #64748b;
                }
                .font-bold {
                    font-weight: 700;
                    color: #1e293b;
                }
                
                .status-badge {
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 700;
                }
                .status-pending { background: #fef3c7; color: #d97706; }
                .status-paid { background: #d1fae5; color: #059669; }
                .status-failed { background: #fee2e2; color: #dc2626; }
                .status-cancelled { background: #f1f5f9; color: #64748b; }
                .status-refunded { background: #e0e7ff; color: #4f46e5; }
                .status-default { background: #f1f5f9; color: #64748b; }

                .checkout-link {
                    color: #6366f1;
                    text-decoration: none;
                    font-weight: 600;
                    padding: 4px 12px;
                    border-radius: 6px;
                    background: #e0e7ff;
                    transition: background 0.2s;
                }
                .checkout-link:hover {
                    background: #c7d2fe;
                }
            `}</style>
        </div>
    );
};

export default AdminFawaterakTransaction;
